import {
  AbsenceCategoryFromJSON,
  AbsenceFromJSON,
  AccessRoleFromJSON,
  BillingCouponRedemptionFromJSON,
  BillingDetailsFromJSON,
  BillingPackageFromJSON,
  BillingPaymentMethodFromJSON,
  BillingProductPriceFromJSON,
  BillingWalletFromJSON,
  BillingWalletTypeFromJSON,
  BudgetSummaryFromJSON,
  ClockInPortalFromJSON,
  CompanyFromJSON,
  CompanyNotificationChannelFromJSON,
  CompanyOnboardingFromJSON,
  CompanySettingFromJSON,
  DaySummaryFromJSON,
  DigiticketsConnectionFromJSON,
  DigiticketsScheduleMapFromJSON,
  DocumentAcceptanceFromJSON,
  DocumentFromJSON,
  DocumentReadFromJSON,
  DocumentVersionFromJSON,
  EmergencyContactFromJSON,
  EmployeeAbsenceSummaryFromJSON,
  EmployeeAttributeFromJSON,
  EmployeeClockingProfileFromJSON,
  EmployeeCostsByJobRoleAndLocationFromJSON,
  EmployeeFromJSON,
  EmployeeGroupFromJSON,
  EmployeeInviteFromJSON,
  EmployeeLeaveBalancesSummaryFromJSON,
  EmployeeLeaveSummaryFromJSON,
  EmployeeMessageFromJSON,
  EmployeeMessageRecipientFromJSON,
  EmployeeNotificationChannelFromJSON,
  EmployeeOnboardingFromJSON,
  EmployeePayDetailsFromJSON,
  EmployeePayPeriodSummaryFromJSON,
  EmployeePersonalDetailsFromJSON,
  FeaturesEnabledFromJSON,
  HistoryFromJSON,
  IcalFeedFromJSON,
  ICRTouchConnectionFromJSON,
  ICRTouchConnectionScheduleSitesConfigFromJSON,
  JobRoleFromJSON,
  LeaveAdjustmentFromJSON,
  LeavePeriodFromJSON,
  LeavePolicyFromJSON,
  LeaveRequestFromJSON,
  LeaveSettingsFromJSON,
  LeaveTypeFromJSON,
  ListEventEntitiesResponseDataEnum,
  LocationFromJSON,
  NotificationSettingsFromJSON,
  OpenShiftResponseFromJSON,
  PayCycleFromJSON,
  PayPeriodFromJSON,
  PayPeriodsSummaryFromJSON,
  PayTypeFromJSON,
  PublicHolidayFromJSON,
  ScheduleEventFromJSON,
  ScheduleFromJSON,
  ScheduleOverviewSummaryFromJSON,
  ScheduleTemplateFromJSON,
  ShiftAreaFromJSON,
  ShiftFromJSON,
  ShiftSwapFromJSON,
  ShiftTemplateFromJSON,
  TagFromJSON,
  TimesheetEntryFromJSON,
  TimesheetEntrySummaryFromJSON,
  TimesheetSettingsFromJSON,
  TrekksoftClientCredentialFromJSON,
  UnavailabilityFromJSON,
  UploadFromJSON,
  UserFromJSON,
  WorkPatternFromJSON,
  WorkPatternOverrideFromJSON,
} from '@/../api/v1';
import {
  absenceApi,
  accessRoleApi,
  ApiListMethodName,
  ApiMethodParams,
  ApiRequest,
  ApiShowMethodName,
  billingApi,
  calendarApi,
  clockInPortalApi,
  companyApi,
  companySettingsApi,
  documentApi,
  emergencyContactApi,
  employeeApi,
  employeeAttributesApi,
  employeeGroupApi,
  employeeInviteApi,
  employeeMessageApi,
  historyApi,
  integrationApi,
  jobRoleApi,
  leaveAdjustmentApi,
  leavePeriodApi,
  leavePolicyApi,
  leaveRequestApi,
  leaveSettingsApi,
  leaveTypeApi,
  locationApi,
  openShiftResponseApi,
  payCycleApi,
  payPeriodApi,
  payTypesApi,
  publicHolidayApi,
  scheduleApi,
  scheduleEventApi,
  scheduleTemplateApi,
  settingsApi,
  shiftApi,
  shiftAreaApi,
  shiftSwapApi,
  shiftTemplateApi,
  summariesApi,
  tagsApi,
  timesheetApi,
  unavailabilityApi,
  uploadApi,
  userApi,
  workPatternApi,
} from '@/api';
import { ApiVersion, ApiVersionTypeMap } from '@/lib/api/types';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { Where } from '@/lib/realtime/weak/realtimeFiltering';
import {
  BatchFetcher,
  CustomEntity,
  EntityConfig,
  EntityFetcherRequestMap,
  EntityInterfaceMap,
  EntityRelationMap,
  Fetcher,
  OrderBy,
  PopulateStrategy,
  WSEntityName,
} from '@/lib/realtime/weak/realtimeTypes';

const genericFetcher =
  <
    Api extends ApiVersionTypeMap[ApiVersion]['BaseApi'],
    MethodName extends ApiListMethodName<ApiVersion, Api>,
    InterfaceMap extends Record<string, object> = EntityInterfaceMap,
    Key extends keyof InterfaceMap = keyof InterfaceMap,
    ParamsMap extends Record<string, object> = EntityFetcherRequestMap,
  >(
    api: Api,
    methodName: MethodName,
    options: {
      with?: string[];
      perPage?: number;
    } = {},
  ): Fetcher<InterfaceMap, Key, ParamsMap> =>
  async (
    params: Key extends keyof ParamsMap ? ParamsMap[Key] : never,
    where: Where<InterfaceMap[Key]>,
    orderBy: OrderBy<InterfaceMap[Key]> = [],
  ): Promise<InterfaceMap[Key][]> =>
    (
      await new ApiRequest(api, methodName, {
        where,
        ...(orderBy.length && { orderBy }),
        ...(options.with && { _with: options.with }),
        perPage: options.perPage ?? 250,
        ...params,
      } as ApiMethodParams<Api[MethodName]>).fetchAll()
    ).data as InterfaceMap[Key][];

const singleEntityFetcher =
  <
    Version extends ApiVersion,
    Api extends ApiVersionTypeMap[Version]['BaseApi'],
    MethodName extends ApiShowMethodName<Version, Api>,
    InterfaceMap extends Record<string, object> = EntityInterfaceMap,
    Key extends keyof InterfaceMap = keyof InterfaceMap,
    ParamsMap extends Record<string, object> = EntityFetcherRequestMap,
  >(
    api: Api,
    methodName: MethodName,
    options: {
      with?: string[];
    } = {},
  ): Fetcher<InterfaceMap, Key, ParamsMap> =>
  async (
    params: Key extends keyof ParamsMap ? ParamsMap[Key] : never,
  ): Promise<InterfaceMap[Key][]> =>
    [
      (await (api[methodName] as Function)({ ...params, ...options })).data,
    ] as InterfaceMap[Key][];

const genericBatchedFetcher =
  <
    Api extends ApiVersionTypeMap[ApiVersion]['BaseApi'],
    MethodName extends ApiListMethodName<ApiVersion, Api>,
    InterfaceMap extends Record<string, object> = EntityInterfaceMap,
    WSSupportName extends keyof InterfaceMap = WSEntityName,
    Key extends keyof InterfaceMap & WSSupportName = InterfaceMap &
      WSSupportName,
  >(
    api: Api,
    methodName: MethodName,
  ): BatchFetcher<InterfaceMap, WSSupportName, Key> =>
  async (ids: (number | string)[]): Promise<InterfaceMap[Key][]> =>
    (
      await new ApiRequest(api, methodName, {
        where: { id: { in: ids } },
        perPage: ids.length,
      } as ApiMethodParams<Api[MethodName]>).fetchAll()
    ).data as InterfaceMap[Key][];

/**
 * This is where we configure how to fetch each entity type from the API.
 *
 * IMPORTANT: When adding to this, you'll also need to update the type mapping in EntityInterfaceMap.
 */
export const entityConfig: EntityConfig<
  EntityInterfaceMap,
  EntityRelationMap,
  ListEventEntitiesResponseDataEnum,
  EntityFetcherRequestMap
> = {
  [Entity.Absence]: {
    fetcher: genericFetcher(absenceApi, 'listAbsence'),
    entityMaker: AbsenceFromJSON,
    orderBy: ['startedOn'],
  },
  [Entity.AbsenceCategory]: {
    fetcher: genericFetcher(absenceApi, 'listAbsenceCategories'),
    entityMaker: AbsenceCategoryFromJSON,
    orderBy: ['name'],
  },
  [Entity.AccessRole]: {
    fetcher: genericFetcher(accessRoleApi, 'listAccessRoles', {
      with: ['permissions'],
    }),
    entityMaker: AccessRoleFromJSON,
    orderBy: ['name'],
  },
  [Entity.BillingCouponRedemption]: {
    fetcher: genericFetcher(billingApi, 'listCouponRedemptions'),
    entityMaker: BillingCouponRedemptionFromJSON,
  },
  [Entity.BillingPackage]: {
    fetcher: genericFetcher(billingApi, 'listBillingPackages'),
    entityMaker: BillingPackageFromJSON,
    orderBy: ['name'],
  },
  [Entity.BillingProductPrice]: {
    fetcher: genericFetcher(billingApi, 'listBillingProductPrices'),
    entityMaker: BillingProductPriceFromJSON,
  },
  [Entity.BillingDetail]: {
    fetcher: singleEntityFetcher(billingApi, 'showBillingDetails'),
    entityMaker: BillingDetailsFromJSON,
    populateStrategy: PopulateStrategy.Disable,
  },
  [Entity.BillingPaymentMethod]: {
    fetcher: genericFetcher(billingApi, 'listPaymentMethods'),
    entityMaker: BillingPaymentMethodFromJSON,
  },
  [Entity.BillingWallet]: {
    fetcher: genericFetcher(billingApi, 'listBillingWallets'),
    entityMaker: BillingWalletFromJSON,
  },
  [Entity.BillingWalletType]: {
    fetcher: genericFetcher(billingApi, 'listBillingWalletTypes'),
    entityMaker: BillingWalletTypeFromJSON,
  },
  [Entity.BudgetSummary]: {
    fetcher: singleEntityFetcher(summariesApi, 'showBudgetSummary'),
    entityMaker: BudgetSummaryFromJSON,
    populateStrategy: PopulateStrategy.Disable,
  },
  [Entity.ClockInPortal]: {
    fetcher: genericFetcher(clockInPortalApi, 'listClockInPortals'),
    relations: [Entity.Location],
    entityMaker: ClockInPortalFromJSON,
  },
  [Entity.Company]: {
    fetcher: genericFetcher(companyApi, 'listCompaniesBelongingToUser'),
    entityMaker: CompanyFromJSON,
    orderBy: ['name'],
  },
  [Entity.CompanyNotificationChannel]: {
    fetcher: genericFetcher(companyApi, 'listCompanyNotificationChannels'),
    entityMaker: CompanyNotificationChannelFromJSON,
  },
  [Entity.CompanySetting]: {
    fetcher: singleEntityFetcher(companySettingsApi, 'showCompanySettings', {
      with: ['logo'],
    }),
    entityMaker: CompanySettingFromJSON,
    populateStrategy: PopulateStrategy.Disable,
  },
  [Entity.DaySummary]: {
    fetcher: singleEntityFetcher(summariesApi, 'showDaySummary'),
    entityMaker: DaySummaryFromJSON,
    populateStrategy: PopulateStrategy.Disable,
  },
  [Entity.DigiticketsConnection]: {
    fetcher: genericFetcher(integrationApi(), 'listDigiticketsConnections'),
    entityMaker: DigiticketsConnectionFromJSON,
  },
  [Entity.DigiticketsScheduleMap]: {
    fetcher: genericFetcher(integrationApi(), 'listDigiticketsScheduleMaps'),
    entityMaker: DigiticketsScheduleMapFromJSON,
  },
  [Entity.Document]: {
    fetcher: genericFetcher(documentApi, 'listDocuments', {
      with: ['latestVersion.upload'],
    }),
    entityMaker: DocumentFromJSON,
  },
  [Entity.DocumentAcceptance]: {
    fetcher: genericFetcher(documentApi, 'listDocumentAcceptances'),
    entityMaker: DocumentAcceptanceFromJSON,
    orderBy: ['createdAt'],
  },
  [Entity.DocumentRead]: {
    fetcher: genericFetcher(documentApi, 'listDocumentReads'),
    entityMaker: DocumentReadFromJSON,
    orderBy: ['createdAt'],
  },
  [Entity.DocumentVersion]: {
    fetcher: genericFetcher(documentApi, 'listDocumentVersions', {
      with: ['upload', 'uploader'],
    }),
    entityMaker: DocumentVersionFromJSON,
    orderBy: ['-version'],
  },
  [Entity.EmergencyContact]: {
    fetcher: genericFetcher(emergencyContactApi, 'listEmergencyContacts'),
    entityMaker: EmergencyContactFromJSON,
    orderBy: ['name'],
  },
  [Entity.Employee]: {
    fetcher: genericFetcher(employeeApi, 'listEmployees', {
      with: ['photo'],
    }),
    relations: [Entity.Location, Entity.JobRole, Entity.Schedule],
    entityMaker: EmployeeFromJSON,
    batchFetcher: genericBatchedFetcher(employeeApi, 'listEmployees'),
    orderBy: ['firstName', 'lastName'],
  },
  [Entity.EmployeeAbsenceSummary]: {
    fetcher: genericFetcher(summariesApi, 'listEmployeeAbsenceSummaries'),
    entityMaker: EmployeeAbsenceSummaryFromJSON,
    populateStrategy: PopulateStrategy.Disable,
  },
  [Entity.EmployeeAttribute]: {
    fetcher: genericFetcher(employeeAttributesApi, 'listEmployeeAttributes'),
    entityMaker: EmployeeAttributeFromJSON,
    orderBy: ['name'],
  },
  [Entity.EmployeeClockingProfile]: {
    fetcher: genericFetcher(employeeApi, 'listEmployeeClockingProfiles'),
    entityMaker: EmployeeClockingProfileFromJSON,
  },
  [Entity.EmployeeGroup]: {
    fetcher: genericFetcher(employeeGroupApi, 'listEmployeeGroups'),
    entityMaker: EmployeeGroupFromJSON,
  },
  [Entity.EmployeeInvite]: {
    fetcher: genericFetcher(employeeInviteApi, 'listEmployeeInvites'),
    entityMaker: EmployeeInviteFromJSON,
    orderBy: ['-createdAt'],
  },
  [Entity.EmployeeLeaveSummary]: {
    fetcher: genericFetcher(summariesApi, 'listEmployeeLeaveSummaries'),
    entityMaker: EmployeeLeaveSummaryFromJSON,
    triggers: [
      Entity.LeaveRequest,
      Entity.LeaveAllowanceChunk,
      Entity.Employee,
      Entity.LeaveAdjustment,
      Entity.LeavePeriod,
    ],
    useWebsocket: false,
    populateStrategy: PopulateStrategy.Disable,
  },
  [Entity.EmployeeMessage]: {
    fetcher: genericFetcher(employeeMessageApi, 'listEmployeeMessages'),
    entityMaker: EmployeeMessageFromJSON,
    orderBy: ['sentAt'],
  },
  [Entity.EmployeeMessageRecipient]: {
    fetcher: genericFetcher(
      employeeMessageApi,
      'listEmployeeMessageRecipients',
    ),
    entityMaker: EmployeeMessageRecipientFromJSON,
  },
  [Entity.EmployeePayDetail]: {
    fetcher: genericFetcher(employeeApi, 'listEmployeePayDetail'),
    entityMaker: EmployeePayDetailsFromJSON,
  },
  [Entity.EmployeePersonalDetail]: {
    fetcher: genericFetcher(employeeApi, 'listEmployeePersonalDetails'),
    entityMaker: EmployeePersonalDetailsFromJSON,
  },
  [Entity.EmployeeNotificationChannel]: {
    fetcher: genericFetcher(employeeApi, 'listEmployeeNotificationChannels'),
    entityMaker: EmployeeNotificationChannelFromJSON,
  },
  [Entity.History]: {
    fetcher: genericFetcher(historyApi, 'listHistory'),
    entityMaker: HistoryFromJSON,
    batchFetcher: genericBatchedFetcher(historyApi, 'listHistory'),
    orderBy: ['changedAt', 'id'],
  },
  [Entity.IcalFeed]: {
    fetcher: genericFetcher(calendarApi, 'listCalendarFeeds'),
    entityMaker: IcalFeedFromJSON,
  },
  [Entity.IcrTouchConnection]: {
    fetcher: genericFetcher(integrationApi(), 'listIcrTouchConnections'),
    entityMaker: ICRTouchConnectionFromJSON,
  },
  [Entity.IcrTouchScheduleConfig]: {
    fetcher: genericFetcher(
      integrationApi(),
      'listIcrTouchConnectionScheduleSitesConfig',
    ),
    entityMaker: ICRTouchConnectionScheduleSitesConfigFromJSON,
  },
  [Entity.JobRole]: {
    fetcher: genericFetcher(jobRoleApi, 'listJobRoles'),
    entityMaker: JobRoleFromJSON,
    orderBy: ['name'],
  },
  [Entity.LeaveAdjustment]: {
    fetcher: genericFetcher(leaveAdjustmentApi, 'listLeaveAdjustments'),
    entityMaker: LeaveAdjustmentFromJSON,
    orderBy: ['createdAt'],
  },
  [Entity.LeavePeriod]: {
    fetcher: genericFetcher(leavePeriodApi, 'listLeavePeriods'),
    batchFetcher: genericBatchedFetcher(leavePeriodApi, 'listLeavePeriods'),
    entityMaker: LeavePeriodFromJSON,
  },
  [Entity.LeavePolicy]: {
    fetcher: genericFetcher(leavePolicyApi, 'listLeavePolicies'),
    entityMaker: LeavePolicyFromJSON,
    orderBy: ['name'],
  },
  [Entity.LeaveRequest]: {
    fetcher: genericFetcher(leaveRequestApi, 'listLeaveRequests', {
      with: ['leaveAllowanceChunks'],
    }),
    batchFetcher: genericBatchedFetcher(leaveRequestApi, 'listLeaveRequests'),
    entityMaker: LeaveRequestFromJSON,
    orderBy: ['startDate'],
    relations: [Entity.LeaveAllowanceChunk],
  },
  [Entity.LeaveSetting]: {
    fetcher: singleEntityFetcher(leaveSettingsApi, 'showLeaveSettings'),
    entityMaker: LeaveSettingsFromJSON,
    populateStrategy: PopulateStrategy.Disable,
  },
  [Entity.LeaveType]: {
    fetcher: genericFetcher(leaveTypeApi, 'listLeaveTypes'),
    entityMaker: LeaveTypeFromJSON,
    orderBy: ['name'],
  },
  [Entity.Location]: {
    fetcher: genericFetcher(locationApi, 'listLocations'),
    entityMaker: LocationFromJSON,
    orderBy: ['name'],
  },
  [Entity.NotificationSetting]: {
    fetcher: singleEntityFetcher(settingsApi, 'showNotificationSettings'),
    entityMaker: NotificationSettingsFromJSON,
    populateStrategy: PopulateStrategy.Disable,
  },
  [Entity.OpenShiftResponse]: {
    fetcher: genericFetcher(openShiftResponseApi, 'listOpenShiftResponses'),
    entityMaker: OpenShiftResponseFromJSON,
    orderBy: ['createdAt'],
  },
  [Entity.PayCycle]: {
    fetcher: genericFetcher(payCycleApi, 'listPayCycles'),
    entityMaker: PayCycleFromJSON,
    orderBy: ['startsOn'],
  },
  [Entity.PayPeriod]: {
    fetcher: genericFetcher(payPeriodApi, 'listPayPeriods'),
    entityMaker: PayPeriodFromJSON,
    orderBy: ['-startsOn'],
  },
  [Entity.PayType]: {
    fetcher: genericFetcher(payTypesApi, 'listPayTypes'),
    entityMaker: PayTypeFromJSON,
    orderBy: ['name'],
  },
  [Entity.PublicHoliday]: {
    fetcher: genericFetcher(publicHolidayApi, 'listPublicHolidays'),
    entityMaker: PublicHolidayFromJSON,
    orderBy: ['date'],
  },
  [Entity.Schedule]: {
    fetcher: genericFetcher(scheduleApi, 'listSchedules'),
    entityMaker: ScheduleFromJSON,
    orderBy: ['name'],
  },
  [Entity.ScheduleEvent]: {
    fetcher: genericFetcher(scheduleEventApi, 'listScheduleEvents'),
    entityMaker: ScheduleEventFromJSON,
    orderBy: ['dateStart'],
  },
  [Entity.ScheduleOverviewSummary]: {
    fetcher: singleEntityFetcher(summariesApi, 'showScheduleOverview'),
    entityMaker: ScheduleOverviewSummaryFromJSON,
    populateStrategy: PopulateStrategy.Disable,
    triggers: [Entity.Shift],
  },
  [Entity.ScheduleTemplate]: {
    fetcher: genericFetcher(scheduleTemplateApi, 'listScheduleTemplates'),
    entityMaker: ScheduleTemplateFromJSON,
  },
  [Entity.Shift]: {
    fetcher: genericFetcher(shiftApi, 'listShifts', {
      with: ['scheduledBreaks', 'shiftConflicts', 'tags'],
    }),
    relations: [Entity.ShiftConflict, Entity.ShiftAreaSession, Entity.Tag],
    entityMaker: ShiftFromJSON,
    batchFetcher: genericBatchedFetcher(shiftApi, 'listShifts'),
    orderBy: ['startsAt'],
  },
  [Entity.ShiftArea]: {
    fetcher: genericFetcher(shiftAreaApi, 'listShiftAreas'),
    relations: [Entity.Location],
    entityMaker: ShiftAreaFromJSON,
    orderBy: ['name'],
  },
  [Entity.ShiftSwap]: {
    fetcher: genericFetcher(shiftSwapApi, 'listShiftSwaps'),
    entityMaker: ShiftSwapFromJSON,
  },
  [Entity.ShiftTemplate]: {
    fetcher: genericFetcher(shiftTemplateApi, 'listShiftTemplates'),
    relations: [Entity.Tag, Entity.ShiftTemplateAreaSession, Entity.JobRole],
    entityMaker: ShiftTemplateFromJSON,
    orderBy: ['startHour', 'startMinute'],
  },
  [Entity.Tag]: {
    fetcher: genericFetcher(tagsApi, 'listTags'),
    entityMaker: TagFromJSON,
    orderBy: ['name'],
  },
  [Entity.TimesheetEntry]: {
    fetcher: genericFetcher(timesheetApi, 'listTimesheetEntries'),
    entityMaker: TimesheetEntryFromJSON,
    batchFetcher: genericBatchedFetcher(timesheetApi, 'listTimesheetEntries'),
    orderBy: ['startedAt'],
  },
  [Entity.TimesheetEntrySummary]: {
    fetcher: genericFetcher(summariesApi, 'listTimesheetSummaries', {
      perPage: 50,
    }),
    entityMaker: TimesheetEntrySummaryFromJSON,
    triggers: [
      Entity.TimesheetEntry,
      Entity.Shift,
      Entity.PayPeriod,
      Entity.Employee,
      Entity.LeaveRequest,
      Entity.LeaveAllowanceChunk,
      Entity.Absence,
    ],
    useWebsocket: false,
    populateStrategy: PopulateStrategy.Disable,
  },
  [Entity.TimesheetSetting]: {
    fetcher: singleEntityFetcher(settingsApi, 'showTimesheetSettings'),
    entityMaker: TimesheetSettingsFromJSON,
  },
  [Entity.TrekksoftClientCredential]: {
    fetcher: genericFetcher(integrationApi(), 'listTrekksoftClientCredentials'),
    entityMaker: TrekksoftClientCredentialFromJSON,
  },
  [Entity.Unavailability]: {
    fetcher: genericFetcher(unavailabilityApi, 'listUnavailability'),
    entityMaker: UnavailabilityFromJSON,
    orderBy: ['startsAt'],
  },
  [Entity.Upload]: {
    fetcher: genericFetcher(uploadApi, 'listUploads'),
    entityMaker: UploadFromJSON,
  },
  [Entity.User]: {
    fetcher: genericFetcher(userApi, 'listCompanyUsers'),
    entityMaker: UserFromJSON,
  },
  [Entity.WorkPattern]: {
    fetcher: genericFetcher(workPatternApi, 'listWorkPatterns'),
    entityMaker: WorkPatternFromJSON,
    orderBy: ['name'],
  },
  [Entity.WorkPatternOverride]: {
    fetcher: genericFetcher(workPatternApi, 'listEmployeeWorkPatternOverrides'),
    entityMaker: WorkPatternOverrideFromJSON,
    batchFetcher: genericBatchedFetcher(
      workPatternApi,
      'listEmployeeWorkPatternOverrides',
    ),
    orderBy: ['date'],
  },
  // Custom Entities
  [CustomEntity.CompanyOnboarding]: {
    fetcher: singleEntityFetcher(companyApi, 'showCompanyOnboarding'),
    entityMaker: CompanyOnboardingFromJSON,
    populateStrategy: PopulateStrategy.Disable,
  },
  [CustomEntity.EmployeeOnboarding]: {
    fetcher: singleEntityFetcher(employeeApi, 'showEmployeeOnboarding'),
    entityMaker: EmployeeOnboardingFromJSON,
    populateStrategy: PopulateStrategy.Disable,
  },
  [CustomEntity.EmployeePayPeriodSummary]: {
    fetcher: genericFetcher(summariesApi, 'listEmployeePayPeriodSummaries'),
    entityMaker: EmployeePayPeriodSummaryFromJSON,
    triggers: [Entity.TimesheetEntry, Entity.PayPeriod],
    useWebsocket: false,
    populateStrategy: PopulateStrategy.Disable,
  },
  [CustomEntity.EmployeeCostsByJobRoleAndLocation]: {
    fetcher: genericFetcher(
      summariesApi,
      'listEmployeeCostsByJobRoleAndLocation',
    ),
    entityMaker: EmployeeCostsByJobRoleAndLocationFromJSON,
    triggers: [Entity.TimesheetEntry, Entity.PayPeriod],
    useWebsocket: false,
    populateStrategy: PopulateStrategy.Disable,
  },
  [CustomEntity.EmployeeLeaveBalancesSummary]: {
    fetcher: genericFetcher(summariesApi, 'listEmployeeLeaveBalanceSummaries'),
    entityMaker: EmployeeLeaveBalancesSummaryFromJSON,
    triggers: [
      Entity.LeaveRequest,
      Entity.LeaveAllowanceChunk,
      Entity.Employee,
      Entity.LeaveAdjustment,
      Entity.LeavePeriod,
      Entity.LeavePolicy,
      Entity.LeaveType,
    ],
    useWebsocket: false,
    populateStrategy: PopulateStrategy.Disable,
  },
  [CustomEntity.FeaturesEnabled]: {
    fetcher: singleEntityFetcher(companyApi, 'showEnabledFeatures'),
    entityMaker: FeaturesEnabledFromJSON,
    useWebsocket: false,
    populateStrategy: PopulateStrategy.Disable,
  },
  [CustomEntity.PayPeriodsSummary]: {
    fetcher: singleEntityFetcher(summariesApi, 'showPayPeriodsSummary'),
    entityMaker: PayPeriodsSummaryFromJSON,
    triggers: [Entity.TimesheetEntry, Entity.PayPeriod],
    useWebsocket: false,
    populateStrategy: PopulateStrategy.Disable,
  },
};
